import { SCHEDULED_WORKFLOW_ERROR_CODES, scheduledWorkflowErrorOf } from "resources/scheduled-workflows/errors"
import type { ScheduledWorkflowErrorCode, ScheduledWorkflowInputField } from "resources/scheduled-workflows/errors"
import { AI_NOT_ACTIVATED_COPY, BLOCKED_COPY, FEATURE_OFF_COPY } from "../scheduled-workflows/blocked-copy"
import { formatResetsAt, formatResetTime, nextBudgetReset, viewerTimeZone } from "../scheduled-workflows/schedule-format"

export type CreateDraftFailure =
  | { kind: "inFlight" }
  | { kind: "retryable" }
  | { kind: "warning", testId: string, message: string, keepsAction: boolean }

const INPUT_FIELD_LABELS: Record<ScheduledWorkflowInputField, string | null> = {
  name: "name",
  description: "description",
  initial_message: "instructions",
  skills: "skills",
  email_results: "email results",
  draft_id: null,
  schedule: "schedule",
  "schedule.type": "schedule type",
  "schedule.days": "days",
  "schedule.time": "time",
  "schedule.timezone": "schedule",
  "schedule.every_hours": "interval",
  "schedule.start": "start time",
  "schedule.end": "end time",
  account_id: null,
  user_id: null,
  limit: null,
  cursor: null,
}

export function inputUnprocessableMessage(name: string, field: ScheduledWorkflowInputField | null): string {
  const label = field === null ? null : INPUT_FIELD_LABELS[field]
  return label === null ? `"${name}" can't be saved as it stands. Ask Kelly to revise it.` : `"${name}" can't be saved as it stands (the ${label}). Ask Kelly to revise it.`
}

export const NO_NOTIFICATION = () => null

export const WORKFLOW_GONE = "This workflow no longer exists."

export function requestValidationMessage(name: string): string {
  return `"${name}" can't be saved as written. Ask Kelly for a revised draft.`
}

export function skillsUnusableMessage(skillNames: string[]): string {
  return `Kelly picked a skill that can't be used here (${skillNames.join(", ")}). Ask Kelly to revise it.`
}

export function skillsReadOnlyMessage(skillNames: string[]): string {
  return `You can read a skill Kelly picked but not edit it (${skillNames.join(", ")}). Ask Kelly to revise it.`
}

function warning(code: ScheduledWorkflowErrorCode, message: string, keepsAction = false): CreateDraftFailure {
  const testId = `kelly-workflow-draft-${code.replace("SCHEDULED_WORKFLOW_", "").toLowerCase().replace(/_/g, "-")}`
  return { kind: "warning", testId, message, keepsAction }
}

export function createDraftFailureOf(error: unknown, name: string, skillName: (skillId: string) => string): CreateDraftFailure {
  const workflowError = scheduledWorkflowErrorOf(error)
  if (workflowError === null) return { kind: "retryable" }
  switch (workflowError.code) {
    case SCHEDULED_WORKFLOW_ERROR_CODES.draftInFlight:
      return { kind: "inFlight" }
    case SCHEDULED_WORKFLOW_ERROR_CODES.limitReached:
      return warning(workflowError.code, `You have ${workflowError.details.limit} workflows, the most allowed. Archive one in Scheduled Workflows, then try again.`, true)
    case SCHEDULED_WORKFLOW_ERROR_CODES.budgetExceeded: {
      const now = new Date()
      const zone = viewerTimeZone()
      const formattedTime = formatResetsAt(workflowError.details.resetsAt, now, zone) ?? formatResetTime(nextBudgetReset(now), now, zone)
      return warning(workflowError.code, `You've reached today's budget — try again after ${formattedTime}.`, true)
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.draftIdConflict:
      return warning(workflowError.code, "This draft was already used — ask Kelly for a new one.")
    case SCHEDULED_WORKFLOW_ERROR_CODES.patAuthorizationError:
      return warning(workflowError.code, "Scheduled workflows need personal access tokens, which are off for this account. An admin can turn them on.")
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsUnresolved:
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsDuplicate:
      return warning(workflowError.code, skillsUnusableMessage(workflowError.details.skills.map(skillName)))
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsWriteAccess:
      return warning(workflowError.code, skillsReadOnlyMessage(workflowError.details.skills.map(skillName)))
    case SCHEDULED_WORKFLOW_ERROR_CODES.inputUnprocessableEntity:
      return warning(workflowError.code, inputUnprocessableMessage(name, workflowError.details.field))
    case SCHEDULED_WORKFLOW_ERROR_CODES.requestValidationUnprocessableEntity:
      return warning(workflowError.code, requestValidationMessage(name))
    case SCHEDULED_WORKFLOW_ERROR_CODES.platformPaused:
      return warning(workflowError.code, BLOCKED_COPY.platform_paused)
    case SCHEDULED_WORKFLOW_ERROR_CODES.credentialInvalid:
      return warning(workflowError.code, BLOCKED_COPY.credential_invalid)
    case SCHEDULED_WORKFLOW_ERROR_CODES.proxyManagedAgentDisabled:
      return warning(workflowError.code, FEATURE_OFF_COPY)
    case SCHEDULED_WORKFLOW_ERROR_CODES.proxyAiNotActivated:
      return warning(workflowError.code, AI_NOT_ACTIVATED_COPY)
    default:
      return { kind: "retryable" }
  }
}
